import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API, formatErr } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useShop } from "../contexts/ShopContext";
import { formatPrice, formatAddress } from "../lib/format";
import { toast } from "sonner";
import { Trash2, Star, FileText, MapPin, Plus, Truck, X, Download } from "lucide-react";

const TABS = [
  { id: "profile", label: "Profile" },
  { id: "orders", label: "Orders" },
  { id: "addresses", label: "Addresses" },
  { id: "wishlist", label: "Wishlist" },
];

export default function Account() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("profile");

  useEffect(() => {
    if (!loading && !user) nav("/login");
  }, [user, loading, nav]);

  if (!user) return null;

  return (
    <div className="pt-32 pb-24 px-6 md:px-12 max-w-5xl mx-auto">
      <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Member</p>
          <h1 className="font-serif text-5xl">{user.name || user.email}</h1>
          <p className="text-muted mt-2">{user.email}</p>
        </div>
        <div className="flex gap-3">
          {user.role === "admin" && <Link to="/admin" className="btn-outline">Admin</Link>}
          <button onClick={async () => { await logout(); nav("/"); }} className="btn-outline" data-testid="logout-btn">Sign Out</button>
        </div>
      </div>

      <div className="flex gap-2 border-b border-[rgba(244,244,240,0.06)] mb-10 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-3 text-xs uppercase tracking-[0.22em] ${tab === t.id ? "text-gold border-b border-[#C8A97E]" : "text-muted hover:text-gold"}`}
            data-testid={`account-tab-${t.id}`}
          >{t.label}</button>
        ))}
      </div>

      {tab === "profile" && <ProfileSection />}
      {tab === "orders" && <OrdersSection />}
      {tab === "addresses" && <AddressesSection />}
      {tab === "wishlist" && <WishlistSection />}
    </div>
  );
}

function ProfileSection() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [saving, setSaving] = useState(false);
  const dirty = name !== (user?.name || "") || phone !== (user?.phone || "");

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/auth/me", { name, phone });
      await refresh();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="max-w-2xl space-y-8" data-testid="profile-section">
      <div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">Contact details</p>
        <h2 className="font-serif text-3xl mb-2">Your profile</h2>
        <p className="text-xs text-muted leading-relaxed">
          We use your mobile number for delivery updates and order tracking. Emails go to <span className="text-gold">{user?.email}</span> — email cannot be changed from here.
        </p>
      </div>

      <div className="space-y-6 bg-[#141414] p-8 border border-[rgba(244,244,240,0.05)]">
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-muted block mb-2">Full name</label>
          <input
            className="input-line w-full"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            placeholder="Priya Sharma"
            data-testid="profile-name-input"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-muted block mb-2">Mobile (10-digit)</label>
          <div className="flex items-stretch">
            <span className="px-3 flex items-center bg-[#0C0C0C] border-b border-[rgba(244,244,240,0.15)] text-muted text-sm">+91</span>
            <input
              className="input-line flex-1"
              type="tel"
              inputMode="numeric"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
              placeholder="9876543210"
              data-testid="profile-phone-input"
              autoComplete="tel"
            />
          </div>
          <p className="text-[10px] text-muted/70 mt-1.5">Must start with 6, 7, 8 or 9. This number is used for Shiprocket delivery calls.</p>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-muted block mb-2">Email (read-only)</label>
          <input className="input-line w-full opacity-50 cursor-not-allowed" value={user?.email || ""} disabled />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={!dirty || saving}
          className="btn-gold disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="profile-save-btn"
        >{saving ? "Saving…" : dirty ? "Save changes" : "Saved"}</button>
        {dirty && (
          <button
            type="button"
            onClick={() => { setName(user?.name || ""); setPhone(user?.phone || ""); }}
            className="btn-outline"
            data-testid="profile-cancel-btn"
          >Discard</button>
        )}
      </div>

      <SecuritySection />
    </form>
  );
}

function SecuritySection() {
  const [openPw, setOpenPw] = useState(false);
  const [openEmail, setOpenEmail] = useState(false);
  return (
    <div className="space-y-4 pt-6 border-t border-[rgba(244,244,240,0.06)]" data-testid="security-section">
      <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Security & sign-in</p>
      <div className="flex flex-wrap gap-3">
        <button type="button" onClick={() => { setOpenPw(v => !v); setOpenEmail(false); }} className="btn-outline text-[10px]" data-testid="toggle-password-btn">
          {openPw ? "Close" : "Change password"}
        </button>
        <button type="button" onClick={() => { setOpenEmail(v => !v); setOpenPw(false); }} className="btn-outline text-[10px]" data-testid="toggle-email-btn">
          {openEmail ? "Close" : "Change email"}
        </button>
      </div>
      {openPw && <ChangePasswordForm onDone={() => setOpenPw(false)} />}
      {openEmail && <ChangeEmailForm onDone={() => setOpenEmail(false)} />}
    </div>
  );
}

function ChangePasswordForm({ onDone }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("New password must be at least 8 characters");
    if (next !== confirm) return toast.error("New password and confirmation don't match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
      onDone?.();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="bg-[#141414] p-6 border border-[rgba(244,244,240,0.05)] space-y-4" data-testid="change-password-form">
      <div>
        <label className="text-[10px] uppercase tracking-[0.22em] text-muted block mb-2">Current password</label>
        <input type="password" className="input-line w-full" value={current} onChange={e => setCurrent(e.target.value)} required minLength={8} data-testid="pw-current" autoComplete="current-password" />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.22em] text-muted block mb-2">New password (min 8 chars)</label>
        <input type="password" className="input-line w-full" value={next} onChange={e => setNext(e.target.value)} required minLength={8} data-testid="pw-new" autoComplete="new-password" />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.22em] text-muted block mb-2">Confirm new password</label>
        <input type="password" className="input-line w-full" value={confirm} onChange={e => setConfirm(e.target.value)} required minLength={8} data-testid="pw-confirm" autoComplete="new-password" />
      </div>
      <button disabled={busy} className="btn-gold disabled:opacity-40" data-testid="pw-submit">{busy ? "Updating…" : "Update password"}</button>
    </form>
  );
}

function ChangeEmailForm({ onDone }) {
  const { user } = useAuth();
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-email/request", { new_email: newEmail, current_password: password });
      setSent(data.sent_to);
      toast.success("Confirmation email sent");
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  if (sent) {
    return (
      <div className="bg-[#141414] p-6 border border-gold/30 space-y-3" data-testid="change-email-sent">
        <p className="text-[10px] uppercase tracking-[0.22em] text-gold">Verification email sent</p>
        <p className="text-sm text-muted leading-relaxed">
          Open <span className="text-gold">{sent}</span> and click the link inside to confirm the change. It's valid for 24 hours. Your current email stays active until you confirm.
        </p>
        <button type="button" onClick={onDone} className="btn-outline text-[10px]" data-testid="change-email-close">Done</button>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="bg-[#141414] p-6 border border-[rgba(244,244,240,0.05)] space-y-4" data-testid="change-email-form">
      <p className="text-xs text-muted">Current email: <span className="text-gold">{user?.email}</span></p>
      <div>
        <label className="text-[10px] uppercase tracking-[0.22em] text-muted block mb-2">New email</label>
        <input type="email" className="input-line w-full" value={newEmail} onChange={e => setNewEmail(e.target.value)} required data-testid="email-new" autoComplete="email" />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.22em] text-muted block mb-2">Current password (to confirm it's you)</label>
        <input type="password" className="input-line w-full" value={password} onChange={e => setPassword(e.target.value)} required minLength={8} data-testid="email-password" autoComplete="current-password" />
      </div>
      <button disabled={busy} className="btn-gold disabled:opacity-40" data-testid="email-submit">{busy ? "Sending…" : "Send verification email"}</button>
    </form>
  );
}

function OrdersSection() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/orders/me").then(({ data }) => setOrders(data)).catch(() => {}); }, []);

  const openInvoice = (orderId) => {
    // Server returns HTML — open in a new tab where the user can Print → Save as PDF
    window.open(`${API}/orders/me/${orderId}/invoice`, "_blank", "noopener");
  };

  const downloadPdf = async (orderId) => {
    try {
      const { data } = await api.get(`/orders/me/${orderId}/invoice.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `GOSHI-invoice-${orderId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  if (orders.length === 0) {
    return <p className="text-muted">No orders yet. <Link to="/shop" className="text-gold underline-offset-4 hover:underline">Begin your edit →</Link></p>;
  }
  return (
    <ul className="space-y-4">
      {orders.map(o => (
        <li key={o.order_id} className="bg-[#141414] p-6" data-testid={`order-${o.order_id}`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-muted">Order</p>
              <p className="font-serif text-lg">{o.order_id}</p>
              <p className="text-xs text-muted mt-1">{new Date(o.created_at).toLocaleDateString()} · {o.items.length} pieces</p>
              <span className="inline-block mt-2 text-[10px] uppercase tracking-[0.18em] border border-gold/40 text-gold px-2 py-0.5">{o.status}</span>
            </div>
            <div className="text-right">
              <p className="font-serif text-2xl text-gold">{formatPrice(o.total)}</p>
              <div className="flex gap-2 mt-3 justify-end flex-wrap">
                <button onClick={() => openInvoice(o.order_id)} className="btn-outline !px-3 !py-2 text-[10px]" data-testid={`invoice-${o.order_id}`} title="View / print invoice">
                  <FileText size={11} strokeWidth={1.5} className="inline mr-1.5 -mt-0.5" />Invoice
                </button>
                <button onClick={() => downloadPdf(o.order_id)} className="btn-outline !px-3 !py-2 text-[10px]" data-testid={`invoice-pdf-${o.order_id}`} title="Download PDF invoice">
                  <Download size={11} strokeWidth={1.5} className="inline mr-1.5 -mt-0.5" />PDF
                </button>
                <Link to={`/track-order?order_id=${o.order_id}`} className="btn-outline !px-3 !py-2 text-[10px]" data-testid={`track-${o.order_id}`}>
                  <Truck size={11} strokeWidth={1.5} className="inline mr-1.5 -mt-0.5" />Track
                </Link>
              </div>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function AddressesSection() {
  const [list, setList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const load = useCallback(() => {
    api.get("/users/me/addresses").then(({ data }) => setList(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const setDefault = async (id) => {
    try {
      const { data } = await api.patch(`/users/me/addresses/${id}/default`);
      setList(data);
      toast.success("Default address updated");
    } catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this address?")) return;
    try {
      const { data } = await api.delete(`/users/me/addresses/${id}`);
      setList(data);
      toast.success("Address removed");
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-[0.25em] text-gold">Saved Addresses ({list.length})</p>
        <button onClick={() => setShowForm(true)} className="btn-outline !px-4 !py-2 text-[10px]" data-testid="add-address-btn">
          <Plus size={11} strokeWidth={1.5} className="inline mr-1.5 -mt-0.5" />Add Address
        </button>
      </div>

      {list.length === 0 ? (
        <p className="text-muted text-sm">No saved addresses. Add one to checkout faster next time.</p>
      ) : (
        <ul className="grid sm:grid-cols-2 gap-4">
          {list.map(a => (
            <li key={a.address_id} className={`bg-[#141414] p-5 relative ${a.is_default ? "border border-gold/40" : ""}`} data-testid={`address-${a.address_id}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-[0.22em] text-gold flex items-center gap-1.5">
                  <MapPin size={11} strokeWidth={1.5} /> {a.label}
                </p>
                {a.is_default && <span className="text-[9px] uppercase tracking-[0.2em] text-gold flex items-center gap-1"><Star size={10} fill="currentColor" /> Default</span>}
              </div>
              <p className="text-sm">{a.name}</p>
              <p className="text-xs text-muted mt-1 leading-relaxed">
                {formatAddress(a)}
              </p>
              <p className="text-xs text-muted mt-2">{a.phone}</p>
              <div className="flex gap-3 mt-4 pt-4 border-t border-[rgba(244,244,240,0.06)]">
                {!a.is_default && (
                  <button onClick={() => setDefault(a.address_id)} className="text-[10px] uppercase tracking-[0.18em] text-muted hover:text-gold" data-testid={`set-default-${a.address_id}`}>Set as default</button>
                )}
                <button onClick={() => remove(a.address_id)} className="text-[10px] uppercase tracking-[0.18em] text-muted hover:text-red-400 ml-auto flex items-center gap-1"><Trash2 size={10} /> Remove</button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showForm && <AddressForm onClose={() => setShowForm(false)} onSaved={(addrs) => { setList(addrs); setShowForm(false); }} />}
    </div>
  );
}

function AddressForm({ onClose, onSaved }) {
  const [form, setForm] = useState({
    label: "Home", name: "", phone: "", flat_no: "", address_line1: "",
    landmark: "", area: "", city: "", state: "", zip: "", country: "India",
    is_default: false,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(s => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !/^[6-9]\d{9}$/.test(form.phone) || !form.address_line1 || !/^\d{6}$/.test(form.zip)) {
      toast.error("Please complete name, valid phone, address and 6-digit PIN");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/users/me/addresses", form);
      onSaved(data);
      toast.success("Address saved");
    } catch (err) { toast.error(formatErr(err)); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={submit} className="relative bg-[#0C0C0C] border border-[rgba(244,244,240,0.08)] w-full max-w-xl max-h-[90vh] overflow-y-auto p-7 space-y-4" data-testid="address-form">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-2xl">Add Address</h3>
          <button type="button" onClick={onClose} className="text-muted hover:text-gold"><X size={20} strokeWidth={1.5} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <select value={form.label} onChange={e => set("label", e.target.value)} className="input-line bg-transparent" data-testid="addr-label">
            <option className="bg-[#0C0C0C]">Home</option>
            <option className="bg-[#0C0C0C]">Work</option>
            <option className="bg-[#0C0C0C]">Other</option>
          </select>
          <input className="input-line" placeholder="Full Name" value={form.name} onChange={e => set("name", e.target.value)} data-testid="addr-name" />
          <input className="input-line" placeholder="Mobile (10-digit)" value={form.phone} onChange={e => set("phone", e.target.value.replace(/\D/g, "").slice(0, 10))} data-testid="addr-phone" />
          <input className="input-line" placeholder="Flat / House No." value={form.flat_no} onChange={e => set("flat_no", e.target.value)} data-testid="addr-flat" />
          <input className="input-line sm:col-span-2" placeholder="Street / Building / Society" value={form.address_line1} onChange={e => set("address_line1", e.target.value)} data-testid="addr-line1" />
          <input className="input-line" placeholder="Landmark (optional)" value={form.landmark} onChange={e => set("landmark", e.target.value)} />
          <input className="input-line" placeholder="Area / Locality" value={form.area} onChange={e => set("area", e.target.value)} />
          <input className="input-line" placeholder="City" value={form.city} onChange={e => set("city", e.target.value)} data-testid="addr-city" />
          <input className="input-line" placeholder="State" value={form.state} onChange={e => set("state", e.target.value)} data-testid="addr-state" />
          <input className="input-line" placeholder="PIN Code" value={form.zip} onChange={e => set("zip", e.target.value.replace(/\D/g, "").slice(0, 6))} data-testid="addr-zip" />
          <input className="input-line" placeholder="Country" value={form.country} onChange={e => set("country", e.target.value)} />
        </div>
        <label className="flex items-center gap-3 text-sm text-muted">
          <input type="checkbox" checked={form.is_default} onChange={e => set("is_default", e.target.checked)} data-testid="addr-default" /> Set as default
        </label>
        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={saving} className="btn-gold flex-1" data-testid="addr-save-btn">{saving ? "Saving…" : "Save Address"}</button>
          <button type="button" className="btn-outline" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </div>
  );
}

function WishlistSection() {
  const { wishlist, toggleWishlist, addToCart } = useShop();

  if (wishlist.length === 0) {
    return <p className="text-muted text-sm">Your wishlist is empty. <Link to="/shop" className="text-gold underline-offset-4 hover:underline">Browse the collection →</Link></p>;
  }
  return (
    <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="account-wishlist">
      {wishlist.map(w => (
        <li key={w.product_id} className="bg-[#141414] p-4 space-y-3">
          <Link to={`/product/${w.product_id}`}>
            <img src={w.image} alt={w.name} className="w-full aspect-[3/4] object-cover" />
          </Link>
          <div>
            <p className="font-serif text-sm">{w.name}</p>
            <p className="text-xs text-gold mt-1">{formatPrice(w.price)}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => addToCart({ product_id: w.product_id, name: w.name, price: w.price, images: [w.image] }, "M", 1)}
              className="btn-outline !px-3 !py-2 text-[10px] flex-1"
              data-testid={`wishlist-add-cart-${w.product_id}`}
            >Add to Bag</button>
            <button
              onClick={() => toggleWishlist({ product_id: w.product_id, name: w.name, price: w.price, images: [w.image] })}
              className="text-muted hover:text-red-400 p-2"
              data-testid={`wishlist-remove-${w.product_id}`}
            ><Trash2 size={14} strokeWidth={1.5} /></button>
          </div>
        </li>
      ))}
    </ul>
  );
}
